/**
 * GP50 RACE SUMMARY table columns (keys = /api/data/race-summary aliases).
 * Speeds arrive in kph and are shown in the project's unit; `kind` drives formatting and deltas.
 */
import { speedUnitBracketUpper, type SpeedDisplayUnit } from "../../../utils/speedUnits";

export type RaceColumnKind = "text" | "int" | "speed" | "pct" | "m" | "sec" | "clock" | "deg" | "ratio";

export interface RaceSummaryColumn {
  key: string;
  header: string;
  group: string;
  kind: RaceColumnKind;
  title?: string;
}

/** Results + distance + speed (table 1). */
export function buildResultsColumns(unit: SpeedDisplayUnit): RaceSummaryColumn[] {
  const U = speedUnitBracketUpper(unit);
  return [
    { key: "source_name", header: "TEAM", group: "", kind: "text" },
    { key: "position", header: "POS", group: "RESULT", kind: "int", title: "Finish position (leg: rounding order)" },
    { key: "positions_lost", header: "±POS", group: "RESULT", kind: "int", title: "Positions lost (+) or gained (-) over the legs" },
    { key: "duration_sec", header: "TIME", group: "RESULT", kind: "clock" },
    { key: "time_behind_leader_sec", header: "BEHIND [S]", group: "RESULT", kind: "sec", title: "Finish (or rounding) time behind the leader" },
    { key: "dtl_end_m", header: "DTL [M]", group: "RESULT", kind: "m", title: "Metres behind along the course when the leader finished (or rounded)" },
    { key: "gap_total_m", header: "GAP [M]", group: "RESULT", kind: "m", title: "Like-for-like finish gap (driver parts sum to this)" },
    { key: "distance_m", header: "SAILED [M]", group: "DISTANCE", kind: "m", title: "Distance sailed, integrated along the track" },
    { key: "course_distance_m", header: "COURSE [M]", group: "DISTANCE", kind: "m", title: "Mark-to-mark distance along the course axis" },
    { key: "distance_ratio", header: "SAILED / COURSE", group: "DISTANCE", kind: "ratio" },
    { key: "bsp_avg_kph", header: `BSP ${U}`, group: "SPEED", kind: "speed" },
    { key: "max_speed", header: `MAX ${U}`, group: "SPEED", kind: "speed" },
    { key: "start_speed", header: `START ${U}`, group: "SPEED", kind: "speed", title: "Boat speed at the gun" },
    { key: "vmg_avg", header: `VMG ${U}`, group: "SPEED", kind: "speed" },
    { key: "vmg_perc_avg", header: "VMG [%]", group: "SPEED", kind: "pct" },
    { key: "vmg_good_perc", header: "VMG>90 [%]", group: "SPEED", kind: "pct" },
    { key: "foiling_perc", header: "FOILING [%]", group: "SPEED", kind: "pct" },
    { key: "gas_avg_perc", header: "GAS [%]", group: "SPEED", kind: "pct" },
  ];
}

/** Course / phase / maneuvers (table 2). */
export function buildCourseTacticsColumns(unit: SpeedDisplayUnit): RaceSummaryColumn[] {
  const U = speedUnitBracketUpper(unit);
  return [
    { key: "source_name", header: "TEAM", group: "", kind: "text" },
    { key: "vmc_avg_kph", header: `VMC ${U}`, group: "COURSE", kind: "speed", title: "Speed made along the course axis, straight-line sailing" },
    { key: "vmc_perc_avg", header: "VMC [%]", group: "COURSE", kind: "pct", title: "VMC as a percentage of target VMG" },
    { key: "inphase_perc", header: "IN PHASE [%]", group: "COURSE", kind: "pct", title: "Share of straight-line time with VMC / VMG ≥ 1" },
    { key: "vmc_ratio_avg", header: "VMC / VMG", group: "COURSE", kind: "ratio" },
    { key: "tack_gybe_count", header: "T + G", group: "MANEUVERS", kind: "int", title: "Tacks + gybes, any grade" },
    { key: "tack_count", header: "T", group: "MANEUVERS", kind: "int" },
    { key: "gybe_count", header: "G", group: "MANEUVERS", kind: "int" },
    { key: "roundup_count", header: "R", group: "MANEUVERS", kind: "int" },
    { key: "bearaway_count", header: "B", group: "MANEUVERS", kind: "int" },
    { key: "tack_loss_avg", header: "TACK LOSS [M]", group: "MANEUVERS", kind: "m" },
    { key: "gybe_loss_avg", header: "GYBE LOSS [M]", group: "MANEUVERS", kind: "m" },
    { key: "roundup_loss_avg", header: "RU LOSS [M]", group: "MANEUVERS", kind: "m" },
    { key: "bearaway_loss_avg", header: "BA LOSS [M]", group: "MANEUVERS", kind: "m" },
    { key: "tws_avg_kph", header: `TWS ${U}`, group: "WIND", kind: "speed" },
    { key: "twd_range_deg", header: "SHIFT [°]", group: "WIND", kind: "deg", title: "TWD range (5th-95th percentile)" },
    { key: "left_side_perc", header: "LEFT [%]", group: "TACTICS", kind: "pct", title: "Share of time left of the course axis, looking upwind" },
    { key: "max_lateral_m", header: "MAX LAT [M]", group: "TACTICS", kind: "m" },
  ];
}

/** Gains / attribution / leverage (table 3). */
export function buildGainsLeverageColumns(): RaceSummaryColumn[] {
  return [
    { key: "source_name", header: "TEAM", group: "", kind: "text" },
    { key: "gap_start_m", header: "START [M]", group: "DRIVERS", kind: "m", title: "Gap booked in the first 30 s after the gun" },
    { key: "gap_mnvr_m", header: "MNVR [M]", group: "DRIVERS", kind: "m", title: "Tacks / gybes (like-for-like)" },
    { key: "gap_marks_m", header: "MARKS [M]", group: "DRIVERS", kind: "m", title: "Mark roundings (like-for-like)" },
    { key: "gap_side_m", header: "SIDE [M]", group: "DRIVERS", kind: "m", title: "Course-half choice" },
    { key: "gap_speed_m", header: "SPEED [M]", group: "DRIVERS", kind: "m", title: "Straight-line speed remainder" },
    { key: "dtl_window_change_m", header: "DTL Δ OWN [M]", group: "GAINS", kind: "m", title: "Change in DTL over the boat's own window (negative = gained)" },
    { key: "dtl_mnvr_m", header: "MNVR Δ [M]", group: "GAINS", kind: "m" },
    { key: "dtl_per_mnvr_m", header: "PER MNVR [M]", group: "GAINS", kind: "m" },
    { key: "dtl_inphase_m", header: "IN PHASE Δ [M]", group: "GAINS", kind: "m" },
    { key: "dtl_outphase_m", header: "OUT PHASE Δ [M]", group: "GAINS", kind: "m" },
    { key: "dtl_other_m", header: "OTHER Δ [M]", group: "GAINS", kind: "m" },
    { key: "leverage_avg_m", header: "LEVERAGE [M]", group: "LEVERAGE", kind: "m" },
    { key: "leverage_max_m", header: "MAX LEV [M]", group: "LEVERAGE", kind: "m" },
    { key: "leverage_side_left_perc", header: "LEFT OF LDR [%]", group: "LEVERAGE", kind: "pct" },
    { key: "shift_gain_m_per_deg", header: "GAIN / ° SHIFT [M]", group: "LEVERAGE", kind: "ratio" },
    { key: "shift_blocks", header: "BLOCKS", group: "LEVERAGE", kind: "int" },
  ];
}

/** All three tables' columns concatenated (legacy single-table views). */
export function buildGp50RaceSummaryColumns(unit: SpeedDisplayUnit): RaceSummaryColumn[] {
  const team = { key: "source_name", header: "TEAM", group: "", kind: "text" as const };
  return [
    team,
    ...buildResultsColumns(unit).filter((c) => c.key !== "source_name"),
    ...buildCourseTacticsColumns(unit).filter((c) => c.key !== "source_name"),
    ...buildGainsLeverageColumns().filter((c) => c.key !== "source_name"),
  ];
}

/** Consecutive columns sharing a group, for the group header row. */
export function columnGroups(cols: RaceSummaryColumn[]): { group: string; span: number }[] {
  const out: { group: string; span: number }[] = [];
  for (const c of cols) {
    const last = out[out.length - 1];
    if (last && last.group === c.group) {
      last.span += 1;
    } else {
      out.push({ group: c.group, span: 1 });
    }
  }
  return out;
}
